/**
 * Responsive Utilities Hook
 * Detects screen size and platform for mobile-friendly UI
 */

import { useState, useEffect } from 'react';
import { isCapacitor, isTauri } from '@/lib/platformUtils';
import { safeIsStandalonePwa } from '@/lib/pwaRuntimeGuards';

export interface ResponsiveState {
  isMobile: boolean;      // Phone screens (< 768px) or mobile platform
  isTablet: boolean;      // Tablet screens (768px - 1024px)
  isDesktop: boolean;     // Desktop screens (>= 1024px)
  isSmallScreen: boolean; // Any screen < 1024px
  screenWidth: number;    // Current screen width
  platform: 'mobile' | 'desktop' | 'web';
}

/**
 * Hook to detect responsive breakpoints and platform
 */
export function useResponsive(): ResponsiveState {
  const [state, setState] = useState<ResponsiveState>(() => {
    // Initial state (SSR-safe)
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isSmallScreen: false,
        screenWidth: 1920,
        platform: 'web'
      };
    }

    const width = window.innerWidth;
    const isMobilePlatform = isCapacitor() || safeIsStandalonePwa();
    const isDesktopPlatform = isTauri();

    return {
      isMobile: width < 768 || isMobilePlatform,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024 && !isMobilePlatform,
      isSmallScreen: width < 1024,
      screenWidth: width,
      platform: isMobilePlatform ? 'mobile' : isDesktopPlatform ? 'desktop' : 'web'
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      const isMobilePlatform = isCapacitor() || safeIsStandalonePwa();
      const isDesktopPlatform = isTauri();

      setState({
        isMobile: width < 768 || isMobilePlatform,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024 && !isMobilePlatform,
        isSmallScreen: width < 1024,
        screenWidth: width,
        platform: isMobilePlatform ? 'mobile' : isDesktopPlatform ? 'desktop' : 'web'
      });
    };

    window.addEventListener('resize', handleResize);
    // Call once immediately to sync
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
}

/**
 * Tailwind-compatible responsive classes
 */
export const responsiveClasses = {
  // Container padding
  containerPadding: 'px-2 sm:px-4 lg:px-6',
  
  // Grid columns
  gridCols: {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  },
  
  // Flexbox
  flexDirection: 'flex-col md:flex-row',
  flexReverse: 'flex-col-reverse md:flex-row',
  
  // Text sizes
  heading: 'text-xl sm:text-2xl lg:text-3xl',
  subheading: 'text-lg sm:text-xl lg:text-2xl',
  body: 'text-sm sm:text-base',
  
  // Spacing
  gap: 'gap-2 sm:gap-4 lg:gap-6',
  margin: 'mx-2 sm:mx-4 lg:mx-6',
  
  // Hidden on mobile
  hideMobile: 'hidden md:block',
  hideMobileInline: 'hidden md:inline',
  hideMobileFlex: 'hidden md:flex',
  
  // Show only on mobile
  showMobile: 'block md:hidden',
  showMobileInline: 'inline md:hidden',
  showMobileFlex: 'flex md:hidden',
  
  // Button sizes
  button: 'px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm md:px-4 md:py-2 md:text-base',
  buttonSmall: 'px-2 py-1 text-xs sm:px-2.5 sm:py-1.5 sm:text-sm',
};
